/*!
 * Cadeau surprise offert
 * - <surprise-gift-sync>     : synchronise la quantite du mini set offert dans le panier
 * - <surprise-gift-carousel> : carrousel d'images (les sets possibles) sur la fiche produit
 */

class SurpriseGiftSync extends HTMLElement {
  constructor() {
    super();

    // Signatures d'operations ayant echoue : on ne boucle pas indefiniment.
    SurpriseGiftSync.failed = SurpriseGiftSync.failed || new Set();
  }

  get variantId() {
    return this.getAttribute('data-variant-id');
  }

  get inCart() {
    return parseInt(this.getAttribute('data-in-cart') || '0', 10);
  }

  get target() {
    return parseInt(this.getAttribute('data-target') || '0', 10);
  }

  connectedCallback() {
    if (this.getAttribute('data-enabled') !== 'true') return;
    if (!this.variantId) return;
    if (SurpriseGiftSync.busy) return;
    if (this.inCart === this.target) return;

    const signature = `${this.variantId}:${this.inCart}:${this.target}`;
    if (SurpriseGiftSync.failed.has(signature)) return;

    this.sync(signature);
  }

  sync(signature) {
    SurpriseGiftSync.busy = true;

    let sectionsToBundle = [];
    document.documentElement.dispatchEvent(new CustomEvent('cart:bundled-sections', { bubbles: true, detail: { sections: sectionsToBundle } }));

    const body = JSON.stringify({
      updates: {
        [this.variantId]: this.target
      },
      sections: sectionsToBundle
    });

    fetch(`${theme.routes.cart_update_url}`, { ...theme.utils.fetchConfig(), ...{ body } })
      .then((response) => response.json())
      .then((parsedState) => {
        if (parsedState.status || parsedState.errors) {
          // Produit cadeau indisponible / introuvable : on arrete pour cette combinaison
          // et on ne republie rien, pour eviter l'alerte native du panier.
          SurpriseGiftSync.failed.add(signature);
          console.warn('[surprise-gift] cadeau non ajoute :', parsedState.description || parsedState.message || parsedState.errors);
          return;
        }

        theme.pubsub.publish(theme.pubsub.PUB_SUB_EVENTS.cartUpdate, { source: 'surprise-gift', cart: parsedState });
      })
      .catch((error) => {
        SurpriseGiftSync.failed.add(signature);
        console.error('[surprise-gift]', error);
      })
      .finally(() => {
        SurpriseGiftSync.busy = false;
      });
  }
}
customElements.define('surprise-gift-sync', SurpriseGiftSync);

class SurpriseGiftCarousel extends HTMLElement {
  get slides() {
    return this._slides = this._slides || Array.from(this.querySelectorAll('[data-sg-slide]'));
  }

  get dots() {
    return this._dots = this._dots || Array.from(this.querySelectorAll('[data-sg-dot]'));
  }

  get speed() {
    return Math.max(600, parseInt(this.getAttribute('data-speed') || '2000', 10));
  }

  connectedCallback() {
    this.index = Math.max(0, this.slides.findIndex((slide) => slide.classList.contains('is-active')));

    if (this.slides.length < 2) return;
    if (window.matchMedia('(prefers-reduced-motion: reduce)').matches) return;

    this.onVisibilityChange = () => (document.hidden ? this.stop() : this.start());
    document.addEventListener('visibilitychange', this.onVisibilityChange);

    if ('IntersectionObserver' in window) {
      this.observer = new IntersectionObserver((entries) => {
        entries[0].isIntersecting ? this.start() : this.stop();
      }, { rootMargin: '100px' });
      this.observer.observe(this);
    }
    else {
      this.start();
    }
  }

  disconnectedCallback() {
    this.stop();
    document.removeEventListener('visibilitychange', this.onVisibilityChange);
    this.observer?.disconnect();
  }

  start() {
    if (this.timer) return;
    this.timer = setInterval(this.next.bind(this), this.speed);
  }

  stop() {
    if (!this.timer) return;
    clearInterval(this.timer);
    this.timer = null;
  }

  next() {
    const previous = this.index;
    this.index = (this.index + 1) % this.slides.length;

    this.slides[previous]?.classList.remove('is-active');
    this.slides[this.index]?.classList.add('is-active');

    this.dots[previous]?.classList.remove('is-active');
    this.dots[this.index]?.classList.add('is-active');
  }
}
customElements.define('surprise-gift-carousel', SurpriseGiftCarousel);
